import { formatFilters } from "utils/filterMapUtils";
import { videoFilterTypes, type VideoOverview, type VideoOverviewRequest } from "features/Library/Videos/types/models";
import type { VideoArgs, VideoContentResponse } from "features/Application/services/dataServices/contentDataService";
import { withCancel } from "features/Application/services/dataServices/utils";
import type { AxiosResponse } from "axios";
import DataService from "features/Application/services/dataServices/typedDataService";
import { createAsyncThunk } from "@reduxjs/toolkit";
import type { FetchActionPayload } from "interfaces";
import { getPrefix } from "features/Application/slices/models";
import { ReducerEntityPrefixTypes, ReducerNamespaceTypes } from "enums/reducer";
import type { RootState } from "features/Application/globaltypes/redux";
import { escapeTerm } from "utils/searchUtils";
import {
  videoAppliedFilterSelector,
  videoSearchSelector,
} from "features/Library/Videos/state/selectors/overviewSelectors";
import { DEFAULT_LOAD_COUNT } from "features/Library/Videos/types/constants";
import type { Filters } from "utils/queryUtils";

export interface VideosRequest {
  appliedFilter: Filters;
  skip: number;
  top: number;
  term: string;
  sortBy?: string;
  sortOrder?: string;
}

const countHeaderName = process.env.REACT_APP_COUNT_HEADER_NAME as string;
export const sendGetVideoRequest = (request: VideosRequest) => {
  const { appliedFilter, skip, top, term, sortBy, sortOrder } = request;
  const filterQueryParams = formatFilters(appliedFilter, videoFilterTypes);

  const requestV4: VideoArgs = {
    top,
    skip,
    sortBy,
    sortOrder,
    filters: filterQueryParams,
    term,
    showPurchased: true,
    contentType: "video",
  };

  const result = withCancel<[VideoArgs], Promise<AxiosResponse<VideoContentResponse[]>>>(
    DataService.content.getContent,
  )(requestV4);

  return result;
};
export const fetchOverviewVideos = createAsyncThunk<FetchActionPayload<VideoOverview>, VideoOverviewRequest>(
  getPrefix({
    namespace: ReducerNamespaceTypes.Library,
    entity: ReducerEntityPrefixTypes.Videos,
    name: "overview",
  }),
  async (requestData: VideoOverviewRequest, { getState, signal }) => {
    const rootState = getState() as RootState;
    const searchTerm = escapeTerm(videoSearchSelector(rootState));
    const appliedFilters = videoAppliedFilterSelector(rootState);

    const { promise, cancel } = sendGetVideoRequest({
      appliedFilter: appliedFilters,
      skip: requestData.skip ?? 0,
      top: requestData.top ?? DEFAULT_LOAD_COUNT,
      term: searchTerm ?? "",
      sortBy: requestData.sortBy,
      sortOrder: requestData.sortDirection,
    });

    signal.addEventListener("abort", cancel);

    const result = await promise;

    const data = result.data.map((item) => ({
      ...item,
      thumbnailUrl: item.bag.thumbnailUrl,
      duration: item.bag.duration,
    }));

    const recordsCount = Number.parseInt(result.headers[countHeaderName]);

    return {
      items: data,
      totalCount: recordsCount,
      append: requestData.append,
    };
  },
);
